import React, { useState, useEffect, useCallback, useRef } from 'react';
import { priceAlertService } from '../services/priceAlertService';
import { coinAnalysisService } from '../services/coinAnalysisService';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useWatchlistStore } from '../stores/useWatchlistStore';
import { blacklistService } from '../services/blacklistService';
import { useToastStore } from '../stores/useToastStore';
import { PriceAlert } from '../types';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import PriceAlertsList from '../components/PriceAlertsList';
import CoinAnalysisDetail from '../components/CoinAnalysisDetail';
import AlertContextMenu from '../components/AlertContextMenu';

const PriceAlertsHistory: React.FC = () => {
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    isVisible: boolean;
    alert: PriceAlert | null;
  }>({ isVisible: false, alert: null });
  const [blacklistedSymbols, setBlacklistedSymbols] = useState<Set<string>>(new Set());
  const setCurrentPage = useSetCurrentPage();
  const loadingRef = useRef(false);
  const offsetRef = useRef(0);

  // 关注功能
  const { addToWatchlist, checkIsWatched } = useWatchlistStore();
  // Toast 提示
  const { show } = useToastStore();

  const fetchAlerts = useCallback(
    async (loadMore = false) => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);
        const currentOffset = loadMore ? offsetRef.current : 0;
        const response = await priceAlertService.getPriceAlerts({
          limit: 20,
          offset: currentOffset,
        });

        if (response) {
          const newAlerts = response.alerts || [];
          console.log('历史警报数据:', newAlerts);
          console.log(
            '新币种告警示例:',
            newAlerts.find((alert: PriceAlert) => alert.isNewCoin || alert.alertType === 'NEW_COIN'),
          );

          if (loadMore) {
            setAlerts((prev) => [...prev, ...newAlerts]);
          } else {
            setAlerts(newAlerts);
          }

          const newOffset = currentOffset + newAlerts.length;
          offsetRef.current = newOffset;
          setHasMore(newAlerts.length === 20);
        }
      } catch (error) {
        console.error('获取历史警报失败:', error);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [], // 空依赖数组，避免重复创建
  );

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // 处理币对点击事件
  const handleAlertClick = (alert: PriceAlert) => {
    setSelectedCoin(alert.symbol);
  };

  // 处理从分析页面返回
  const handleBackFromAnalysis = () => {
    setSelectedCoin(null);
  };

  // 处理长按告警项
  const handleAlertLongPress = (alert: PriceAlert) => {
    setContextMenu({
      isVisible: true,
      alert,
    });
  };

  // 关闭上下文菜单
  const handleCloseContextMenu = () => {
    setContextMenu({
      isVisible: false,
      alert: null,
    });
  };

  // 添加关注
  const handleAddToWatchlist = async (symbol: string, instType: string) => {
    try {
      await addToWatchlist(symbol, instType);
      // 可以显示成功提示
      console.log(`已关注 ${symbol} (${instType})`);
    } catch (error: any) {
      console.error('添加关注失败:', error.message);
      // 可以显示错误提示
    }
  };

  // 检查是否已关注
  const isAlertWatched = (alert: PriceAlert) => {
    if (!alert.instType) return false;
    return checkIsWatched(alert.symbol, alert.instType);
  };

  // 检查是否在黑名单中
  const isAlertBlacklisted = (alert: PriceAlert) => {
    return blacklistedSymbols.has(alert.symbol);
  };

  // 添加黑名单
  const handleAddToBlacklist = async (symbol: string) => {
    try {
      await blacklistService.addToBlacklist(symbol);
      setBlacklistedSymbols((prev) => new Set(prev).add(symbol));
      show('已加入黑名单', 'success');
    } catch (error: any) {
      console.error('添加黑名单失败:', error);
      show(error.message || '添加黑名单失败', 'error');
    }
  };

  // 请求AI分析
  const handleRequestAIAnalysis = async (symbol: string) => {
    try {
      show('正在添加到分析队列...', 'info');
      // 如果调用成功，response 是数据负载，没有 success 字段
      await coinAnalysisService.addCoinToAnalysisQueue(symbol);
      show('币对已添加到AI分析队列，分析完成后可在分析页面查看', 'success');
    } catch (error: any) {
      console.error('请求AI分析失败:', error);
      show(error.response?.data?.message || error.message || '请求AI分析失败', 'error');
    }
  };

  // 加载黑名单状态
  useEffect(() => {
    const loadBlacklist = async () => {
      try {
        const blacklist = await blacklistService.getBlacklist();
        const symbols = new Set(blacklist.map((item) => item.symbol));
        setBlacklistedSymbols(symbols);
      } catch (error) {
        console.error('加载黑名单失败:', error);
      }
    };
    loadBlacklist();
  }, []);

  // 滚动加载更多
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
        fetchAlerts(true);
      }
    },
    [hasMore, loading, fetchAlerts],
  );

  // 搜索：调用后端接口（不限制时间），防抖 400ms
  useEffect(() => {
    let timer: any;
    const run = async () => {
      if (!search.trim()) {
        // 搜索为空时，恢复历史分页数据
        fetchAlerts();
        setHasMore(true);
        return;
      }
      try {
        setLoading(true);
        const response = await priceAlertService.searchPriceAlerts(search.trim(), 100, 0);
        if (response?.alerts) {
          setAlerts(response.alerts);
          setHasMore(false); // 搜索结果不做滚动分页
        }
      } catch (e) {
        console.error('搜索历史价格警报失败', e);
      } finally {
        setLoading(false);
      }
    };
    timer = setTimeout(run, 400);
    return () => clearTimeout(timer);
  }, [search, fetchAlerts]);

  // 如果选择了币对，显示分析详情页面
  if (selectedCoin) {
    return <CoinAnalysisDetail instId={selectedCoin} onBack={handleBackFromAnalysis} />;
  }

  // 过滤（不限制时间，仅基于内容匹配）
  const normalized = (v: any) => (v ?? '').toString().toLowerCase();
  const filteredAlerts = alerts.filter((a) => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return (
      normalized(a.symbol).includes(q) || normalized(a.alertType).includes(q) || normalized(a.timeFrame).includes(q)
    );
  });

  return (
    <GradientBackground>
      <div className="h-screen overflow-hidden flex flex-col">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="历史价格警报" onBack={() => setCurrentPage('dashboard')} />

        {/* 搜索框 */}
        <div className="px-4 pt-16 pb-3 flex-shrink-0">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索币对/类型/时间粒度..."
              className="w-full bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 px-10 py-2 focus:outline-none focus:border-white/40"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 text-sm">🔎</span>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/80"
              >
                清除
              </button>
            )}
          </div>
        </div>

        {/* 滚动列表 */}
        <div className="flex-1 overflow-y-auto px-4 pt-0" onScroll={handleScroll}>
          <PriceAlertsList
            alerts={filteredAlerts}
            timeFormat="history"
            onAlertClick={handleAlertClick}
            onAlertLongPress={handleAlertLongPress}
            isAlertWatched={isAlertWatched}
          />

          {/* 加载状态 */}
          {loading && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto"></div>
            </div>
          )}

          {/* 没有更多数据 */}
          {!hasMore && alerts.length > 0 && (
            <div className="text-center py-4">
              <div className="text-white/40 text-sm">没有更多数据了</div>
            </div>
          )}

          {/* 空状态 */}
          {alerts.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400">暂无历史警报</div>
            </div>
          )}
        </div>

        {/* 上下文菜单 */}
        {contextMenu.isVisible && contextMenu.alert && (
          <AlertContextMenu
            alert={contextMenu.alert}
            isVisible={contextMenu.isVisible}
            onClose={handleCloseContextMenu}
            onAddToWatchlist={handleAddToWatchlist}
            isWatched={isAlertWatched(contextMenu.alert)}
            onAddToBlacklist={handleAddToBlacklist}
            isBlacklisted={isAlertBlacklisted(contextMenu.alert)}
            onRequestAIAnalysis={handleRequestAIAnalysis}
          />
        )}
      </div>
    </GradientBackground>
  );
};

export default PriceAlertsHistory;
